/**
 * The turning body on the Waking screen.
 *
 * ## The file header this replaces
 * `Waking.tsx` used to argue — at length, and correctly for what it was then — that the bodies
 * on this screen should be photographs: a live turntable meant a second three.js renderer inside
 * the hub, on the cold-start screen, before the player had committed to anything. That argument
 * was written when a body was a `char-0*.glb` skinned rig and the choice was cosmetic. It is not
 * that any more. Since the pivot the choice is a **race**, it is written to the character row
 * once and never again, and the thing it picks is the silhouette the whole game draws you with.
 * A player deciding that from two PNGs is deciding it from someone else's screenshot.
 *
 * So the turntable is back, and the objection is answered rather than ignored:
 *
 * - **Lazy.** Nothing here is constructed until the screen is actually shown, and most sessions
 *   never show it. `dispose()` on hide tears the device down again.
 * - **Fail-soft, visibly.** Everything is inside one `try`. If `renderer.init()` throws — no
 *   WebGPU, no WebGL2, a device already lost — the caller is told and falls back to the
 *   photographs, which are still on disk. A first impression must never be an error message.
 * - **One body, not three hundred.** `maxSlots: 1`, no nameplates, no blob shadow field to
 *   speak of, one directional-ish light baked into the crowd material's uniforms. The cost is
 *   the baked animation texture for the two races, which is the same asset the game itself is
 *   about to load and which the browser therefore serves from cache a moment later.
 *
 * ## Why it reuses the crowd rather than loading a GLB
 * `render/crowd.ts` is the only thing in the client that knows how to draw a STILLSUN body, and
 * the whole point of this screen is that what you pick is what you get. Standing a second,
 * different renderer up here would put a body on the creation screen that no other screen can
 * reproduce. This is the `crowd.html` bench's scene (`src/dev/crowd-bench.ts`), minus the crowd:
 * a renderer, a ground plane, the sun uniforms, and exactly one character.
 */

import {
  Color,
  Mesh,
  MeshBasicNodeMaterial,
  PerspectiveCamera,
  PlaneGeometry,
  Scene,
  Timer,
  Vector3,
  WebGPURenderer,
} from 'three/webgpu';
import { AnimState, type LookParts } from '@stillsun/shared';
import { CROWD_BODIES, CrowdField, setCrowdSun } from '../../render/crowd.js';

/** How fast the body turns, radians per second. A full revolution in about twenty seconds. */
const TURN_RATE = 0.32;
/**
 * Camera distance and eye height, metres, and what it looks at.
 *
 * Framed a little above the waist and close enough that the body fills the stage rather than
 * standing in the middle of it: this is a portrait of the thing being chosen, and at three
 * metres the KayKit silhouette — which is the entire point of the choice — was a doll in a
 * box with a third of the panel empty above its head.
 */
const CAM_DIST = 2.85;
const CAM_EYE = 1.15;
const LOOK_AT = 1.02;

export class WakingPreview {
  private renderer: WebGPURenderer | null = null;
  private crowd: CrowdField | null = null;
  private slot = -1;
  private disposed = false;
  /**
   * The canvas is a flex child of a grid column, so its box is not settled when the preview is
   * built and it keeps moving as the copy under it changes length. A `window resize` listener
   * alone measured it once, early and wrong, and the body was then drawn against an aspect the
   * element no longer had — a dark band across the top and the feet outside the painted area.
   */
  private observer: ResizeObserver | null = null;
  private readonly timer = new Timer();
  private yaw = Math.PI;
  private look: LookParts = {};
  /** Last size handed to the renderer, so an observer that fires per frame costs nothing. */
  private lastW = 0;
  private lastH = 0;

  private constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly scene: Scene,
    private readonly camera: PerspectiveCamera,
  ) {}

  /**
   * Stand the preview up on `canvas`. Resolves `null` — never throws — when this browser or
   * this moment cannot give us a device; the caller then shows the photographs.
   */
  static async create(canvas: HTMLCanvasElement): Promise<WakingPreview | null> {
    let renderer: WebGPURenderer | null = null;
    try {
      renderer = new WebGPURenderer({ canvas, antialias: true, alpha: true });
      await renderer.init();
      renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
      renderer.setClearColor(new Color(0x0e0a1c), 0);

      const scene = new Scene();
      // Nothing in this scene moves except through the crowd's state texture, so three has no
      // reason to walk the graph on its own — the same trick the game scene uses.
      scene.matrixWorldAutoUpdate = false;

      const camera = new PerspectiveCamera(34, 1, 0.1, 40);

      // A ground plane so the body is standing on something rather than floating in the plate.
      const ground = new Mesh(
        new PlaneGeometry(24, 24),
        new MeshBasicNodeMaterial({ color: new Color(0x181233) }),
      );
      ground.rotation.x = -Math.PI / 2;
      ground.updateMatrix();
      ground.matrixAutoUpdate = false;
      ground.updateMatrixWorld(true);
      scene.add(ground);

      // Requiem lighting: a cool lilac key from the front-left and a warmer bounce, so a Hollow
      // reads as bone rather than as grey and a Vanguard's plate has somewhere to catch.
      setCrowdSun(
        new Vector3(-0.42, 0.74, 0.52),
        new Color(0.98, 0.94, 1.0),
        new Color(0.3, 0.26, 0.44),
        new Color(0.72, 0.66, 1.0),
      );

      const preview = new WakingPreview(canvas, scene, camera);
      preview.renderer = renderer;

      // No nameplates: the marker root is a detached div this screen never shows, because the
      // one body on it is the player and they know who they are.
      const crowd = await CrowdField.create(scene, document.createElement('div'), {
        bodies: CROWD_BODIES,
        maxSlots: 1,
        drawDistance: 40,
        maxPlates: 0,
      });
      if (preview.disposed) {
        crowd.dispose();
        void renderer.dispose();
        return null;
      }
      preview.crowd = crowd;
      preview.slot = crowd.acquire();
      if (preview.slot >= 0) crowd.setLook(preview.slot, preview.look);

      preview.resize();
      // Re-measure whenever the element's box actually changes, rather than only when the
      // window does. Guarded: `ResizeObserver` is everywhere WebGPU is, but a preview that
      // throws here would take the whole screen's turntable with it.
      try {
        preview.observer = new ResizeObserver(() => {
          preview.resize();
        });
        preview.observer.observe(canvas);
      } catch {
        /* no observer: the window listener in Waking.tsx is the fallback */
      }
      await renderer.setAnimationLoop((ms) => {
        preview.frame(ms);
      });
      return preview;
    } catch (err) {
      // Not an error the player is shown. The photographs are still on disk and the screen
      // works without this; all the console line is for is telling us it happened.
      console.info('[stillsun] waking preview unavailable; using the photographs', err);
      try {
        await renderer?.dispose();
      } catch {
        /* the device is already gone, which is the case we are in */
      }
      return null;
    }
  }

  /** Race and Calling, straight into the same `look` word the crowd draws every body from. */
  setLook(look: LookParts): void {
    this.look = look;
    if (this.crowd && this.slot >= 0) this.crowd.setLook(this.slot, look);
  }

  /** The canvas element changed size (a window resize, or the column reflowing under it). */
  resize(): void {
    const renderer = this.renderer;
    if (!renderer) return;
    const w = Math.max(1, this.canvas.clientWidth);
    const h = Math.max(1, this.canvas.clientHeight);
    if (w === this.lastW && h === this.lastH) return;
    this.lastW = w;
    this.lastH = h;
    renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private frame(timeMs: number): void {
    const renderer = this.renderer;
    const crowd = this.crowd;
    if (!renderer || !crowd || this.slot < 0) return;
    this.timer.update(timeMs);
    const dt = Math.min(this.timer.getDelta(), 0.1);

    this.yaw += dt * TURN_RATE;
    crowd.setState(this.slot, ORIGIN, this.yaw, AnimState.Idle, 1);

    this.camera.position.set(0, CAM_EYE, CAM_DIST);
    this.camera.lookAt(0, LOOK_AT, 0);
    this.camera.updateMatrixWorld();
    VIEWER.x = this.camera.position.x;
    VIEWER.y = this.camera.position.y;
    VIEWER.z = this.camera.position.z;

    crowd.update(
      dt,
      this.camera,
      VIEWER,
      timeMs,
      this.canvas.clientWidth,
      this.canvas.clientHeight,
    );
    this.scene.updateMatrixWorld();
    renderer.render(this.scene, this.camera);
  }

  /** Give the device back. Called the moment the screen is hidden — see `mountWaking`. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    const renderer = this.renderer;
    this.renderer = null;
    try {
      this.observer?.disconnect();
      this.observer = null;
      void renderer?.setAnimationLoop(null);
      this.crowd?.dispose();
      void renderer?.dispose();
    } catch (err) {
      console.info('[stillsun] waking preview teardown', err);
    }
    this.crowd = null;
    this.slot = -1;
  }
}

const ORIGIN = { x: 0, y: 0, z: 0 };
const VIEWER = { x: 0, y: CAM_EYE, z: CAM_DIST };
